import type { Role } from "@prisma/client";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type SessionUser = {
  id: string;
  email: string;
  role: Role;
  /** Id del User superadmin que originó esta sesión, cuando es una impersonación. */
  impersonatedBy: string | null;
};

type Ctx = {
  user: SessionUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch("/api/auth/session", { credentials: "include" });
      const ct = r.headers.get("content-type") ?? "";
      if (!r.ok || !ct.includes("application/json")) {
        setUser(null);
        return;
      }
      const d = (await r.json()) as { user: SessionUser | null };
      setUser(d.user);
    } catch {
      // API aún no levantó o red caída: tratamos como sesión ausente (evita ruido y estados rotos).
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void refresh().finally(() => setLoading(false));
  }, [refresh]);

  /** Al volver a la pestaña, alineamos el estado con la cookie (evita UI “logueada” con API 401/403). */
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  const value = useMemo(() => ({ user, loading, refresh }), [user, loading, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const c = useContext(AuthContext);
  if (!c) throw new Error("useAuth");
  return c;
}
